import {
    OnInit,
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    ChangeDetectorRef,
    ViewContainerRef,
    signal,
} from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';

// Classes
import { BaseFilter } from '@finder/shared/components/filters/classes/base-filter.class';
import { DrawerFilterButton } from '@finder/features/list/components/filters/classes/drawer-filter-button.class';
import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { FilterGroup } from '@finder/features/list/components/filters/classes/filter-group.class';
import { TopFilterButton } from '@finder/shared/components/filters/classes/top-filter-button.class';
// Services
import { HeightSliderService } from '@finder/features/list/components/filters/components/height-slider/height-slider.service';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { LocalStorageService } from '@finder/shared/services/local-storage/local-storage.service';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
// Components
import { FilterDrawerComponent } from '@finder/features/list/components/filters/components/filter-drawer/filter-drawer.component';
import { HeightSliderComponent } from '@finder/features/list/components/filters/components/height-slider/height-slider.component';
// eslint-disable-next-line max-len
import { MobileAllFiltersComponent } from '@finder/features/list/components/filters/components/mobile-all-filters/mobile-all-filters.component';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
// Others
import { FilterDrawerToggle } from '@finder/features/list/components/filters/classes/filter-drawer-toggle.interface';
import { CommonModule } from '@angular/common';
import { FilterButtonComponent } from '@finder/shared/components/filters/components/filter-button/filter-button.component';
import { ListDrawerComponent } from './components/list-drawer/list-drawer.component';
import { DineAvailabilityFilterComponent } from './components/dine-availability-filter/dine-availability-filter.component';
import { FilterPillsComponent } from '@finder/shared/components/filters/components/filter-pills/filter-pills.component';
import { asyncScheduler } from 'rxjs';

@Component({
    selector: 'finder-list-filters',
    templateUrl: './list-filters.component.html',
    styleUrls: ['./list-filters.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FilterButtonComponent,
        ListDrawerComponent,
        HeightSliderComponent,
        DineAvailabilityFilterComponent,
        FilterPillsComponent,
    ]
})
export class ListFiltersComponent extends BaseFilter implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('drawersPlaceholder', { read: ViewContainerRef, static: false }) drawersContainer: ViewContainerRef;

    heightFilterButton: DrawerFilterButton;
    heightFilterModal: ModalComponent;
    drawerFilters: FilterDrawerToggle[];
    heightButtonSg = signal<boolean>(false);

    private heightBtnElm;

    constructor(
        private heightSliderService: HeightSliderService,
        private modalService: ModalService,
        private changeDetector: ChangeDetectorRef,
        protected filterEmitterService: FilterEmitterService,
        protected finderAnalyticsService: FinderAnalyticsService,
        protected viewport: ViewportDetectionService,
        protected localStorageService: LocalStorageService,
    ) {
        super(filterEmitterService, finderAnalyticsService, localStorageService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.viewport.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.heightFilterButton) {
                    this.checkHeightFilterContainer();
                }
                if (this.drawerFilters) {
                    this.checkDrawersContainer();
                }
            });
    }

    ngAfterViewInit() {
        this.mapDrawers();
        this.mapHeightFilter();
        super.ngAfterViewInit();
        this.changeDetector.detectChanges();
    }

    ngOnDestroy() {
        this.heightSliderService.disconnect();
    }

    clearPills() {
        super.clearPills();
        this.heightSliderService.clearSelectedFacetAndValue();
    }

    handleWatchFacet(facet: Facet) {
        if (this.topFilters) {
            this.topFilters.forEach((filter: TopFilterButton) => this.setFilterState(filter, facet));
        }
        if (this.heightFilterButton) {
            this.setFilterState(this.heightFilterButton, facet);
        }
        if (this.drawerFilters) {
            this.drawerFilters.forEach(drawer => {
                this.setFilterState(drawer.button, facet);
            });
        }
        this.applyFacet(facet);
    }

    toggleHeightFilter() {
        if (this.viewport.isMobile()) {
            this.openHeightFilterModal();

            return;
        }

        this.heightFilterButton.toggleDisplayedState();
    }

    toggleDrawer(filter: FilterDrawerToggle, event: Event) {
        if (!this.viewport.isDesktop()) {
            filter.modal.open();

            return;
        }

        const listDrawer = filter.drawer.listDrawer;
        if (!listDrawer.sourceElement) {
            const element = event.composedPath()
                .filter(e => e instanceof HTMLElement)
                .find((e: HTMLElement) => e.classList.contains('filter-button'));

            listDrawer.sourceElement = element as HTMLElement;
            listDrawer.setUpListeners();
        }
        listDrawer.toggleDisplayedState();
    }

    /**
     * The get/set method here allow us to reference an element in one
     * part of the template in another. You can't do this within the template
     * because variables are scoped to the tag.
     * This was also done to remove a ViewChild reference that caused
     * ExpressionChangedAfterItHasBeenCheckedError errors when it starts and undefined
     * and then becomes an object after the template has updated itself. This was also
     * solved using a Signal below.
     */
    setHeightBtn(elm) {
        this.heightBtnElm = elm;
    }

    getHeightBtn() {
        return this.heightBtnElm || {};
    }

    private mapHeightFilter() {
        const heightFilter = this.filters.heightFilter;
        if (!heightFilter) {
            return;
        }

        this.heightSliderService.initialize(heightFilter.facets, heightFilter.anyHeightFacet, heightFilter.shorterFacets);
        this.heightSliderService.facetsChanged()
            .pipe(takeUntil(this.destroy$))
            .subscribe((facets) => {
                facets.forEach((facet) => {
                    this.applyFacet(facet);
                });
            });

        this.heightFilterButton = new DrawerFilterButton(
            heightFilter.id,
            heightFilter.labels.open,
            'icon_ride-height',
            this.filters.labels.buttonAriaLabel,
            heightFilter.facets,
            heightFilter.labels.close
        );

        // Let the template know the button is ready
        asyncScheduler.schedule(() => {
            this.heightButtonSg.set(true);
        });
    }

    private mapDrawers() {
        if (!this.filters.drawerFilters) {
            return;
        }
        this.drawerFilters = this.filters.drawerFilters.map((filter) => {
            const filterGroups = filter.filterGroups.map(filterGroup => new FilterGroup(
                filterGroup.label,
                filterGroup.displayLimit,
                filterGroup.layoutConfig,
                filterGroup.toolTipText,
                filterGroup.facetGroups,
                filterGroup.group
            ));

            const button = new DrawerFilterButton(
                filter.id,
                filter.labels.open,
                filter.icon,
                this.filters.labels.buttonAriaLabel,
                filter.facets,
                filter.labels.close
            );

            const drawerRef = this.drawersContainer.createComponent(FilterDrawerComponent);

            drawerRef.instance.filters = filterGroups;
            drawerRef.instance.shrinkButtons = this.shrinkButtons;
            drawerRef.instance.labels = this.filters.labels;
            drawerRef.instance.useHeightFilter = filter.useHeightFilter;
            // bind drawer state to button state
            drawerRef.instance.displayedStateChanged$
                .pipe(takeUntil(this.destroy$))
                .subscribe((state) => {
                    button.displayedDrawer = state;
                    button.setSelectedState();
                });

            const mobileModal = this.modalService.create(
                MobileAllFiltersComponent,
                {
                    title: filter.labels.open,
                    data: {
                        allFilters: filterGroups,
                        appliedFacets: this.appliedFacets,
                        useHeightFilter: filter.useHeightFilter,
                        showFilterPills: true,
                        labels: {
                            done: this.filters.labels.done,
                            height: this.filters.labels.height,
                            clearAll: this.filters.labels.clearAll,
                            pillAriaLabel: this.filters.labels.pillAriaLabel,
                            pillsWrapper: this.filters.labels.pillsWrapper
                        }
                    }
                }
            );

            return {
                showTotal: filter.showTotal,
                slim: filter.slim,
                filters: filterGroups,
                button,
                modal: mobileModal,
                drawer: drawerRef.instance
            };
        });
    }

    private checkHeightFilterContainer() {
        if (this.heightFilterButton.displayedDrawer && this.viewport.isMobile()) {
            this.heightFilterButton.displayedDrawer = false;
            this.toggleHeightFilter();
        } else if (
            this.heightFilterModal &&
            this.heightFilterModal.is('opened') &&
            !this.viewport.isMobile()
        ) {
            this.heightFilterModal.dismiss();
            this.heightFilterButton.toggleDisplayedState();
        }
    }

    private openHeightFilterModal() {
        if (this.heightFilterModal) {
            this.heightFilterModal.open();

            return;
        }

        this.heightFilterModal = this.modalService.create(
            HeightSliderComponent,
            {
                title: this.filters.heightFilter.labels.sectionTitle,
                subtitle: this.filters.heightFilter.labels.sectionSubtitle
            }
        );

        this.heightFilterModal.ready()
            .pipe(take(1))
            .subscribe(() => this.heightFilterModal.open());
    }

    private checkDrawersContainer() {
        this.drawerFilters.forEach(filter => {
            const listDrawer = filter.drawer.listDrawer;
            if (listDrawer.displayedState && !this.viewport.isDesktop()) {
                filter.modal.open();
                listDrawer.toggleDisplayedState();
            } else if (
                filter.modal.is('opened') &&
                this.viewport.isDesktop()
            ) {
                filter.modal.dismiss();
                listDrawer.toggleDisplayedState();
            }
        });
    }

}
